import { useEffect, useRef } from 'react';
import { OperationData } from '../../../types/OperationData';
import { telegramApi } from '../../../utils/telegramApi';
import { GeolocationType } from './useGeolocation';

/**
 * Хук для управления главной кнопкой Telegram Web App.
 * 
 * Ответственность:
 * - Динамическое обновление текста и состояния кнопки
 * - Управление видимостью кнопки
 * - Подключение обработчиков событий
 * - Очистка ресурсов при размонтировании
 * 
 * @param images - массив изображений
 * @param location - данные геолокации
 * @param operationData - данные операции
 * @param onSend - функция отправки фотографий
 */
export function useTelegramButton(
    images: string[],
    location: GeolocationType | null,
    operationData: OperationData | null,
    onSend: () => void
) {
    const handleSendRef = useRef(onSend);
    handleSendRef.current = onSend;

    useEffect(() => {
        // При монтировании всегда скрываем главную кнопку и сбрасываем обработчик
        telegramApi.mainButton.hide();
        telegramApi.mainButton.offClick();
    }, []);

    /**
     * Управляет состоянием и поведением главной кнопки Telegram Web App.
     * 
     * Динамически обновляет текст кнопки в зависимости от состояния приложения:
     * - "Необходима геолокация" - когда геолокация не получена
     * - "Сделайте минимум X фото" - когда фото меньше минимального количества
     * - "Максимум X фото" - когда превышен лимит фотографий
     * - "Отправить X фото" - когда все условия выполнены для отправки
     * 
     * Скрывает кнопку полностью при отсутствии фотографий.
     */
    useEffect(() => {
        const minImages = operationData?.min_images || 1;
        const maxImages = operationData?.max_images || 10;
        const canSend = !!(location && images.length >= minImages && images.length <= maxImages);

        let buttonText: string;
        if (!location) {
            buttonText = 'Необходима геолокация';
        } else if (images.length < minImages) {
            buttonText = `Сделайте минимум ${minImages} фото`;
        } else if (images.length > maxImages) {
            buttonText = `Максимум ${maxImages} фото`;
        } else {
            buttonText = `Отправить ${images.length} фото`;
        }

        // Если фотографий нет, скрываем кнопку и сбрасываем обработчик
        if (images.length === 0) {
            telegramApi.mainButton.hide();
            telegramApi.mainButton.offClick();
            return;
        }

        // Показываем кнопку с текстом и состоянием
        telegramApi.mainButton.show(buttonText, canSend);
        if (canSend) {
            telegramApi.mainButton.enable();
        } else {
            telegramApi.mainButton.disable();
        }
        // Сбросить предыдущий обработчик и установить новый
        telegramApi.mainButton.offClick();
        telegramApi.mainButton.onClick(() => {
            if (canSend) {
                handleSendRef.current();
            }
        });
    }, [images, location, operationData]);

    // Очистка при размонтировании компонента
    useEffect(() => {
        return () => {
            telegramApi.mainButton.reset();
        };
    }, []);
}